import { useState, useEffect, useRef, memo } from "react";
import { callDays } from "@/lib/constants";
import { generateTimeSlots } from "@/lib/utils";
import { CheckGroupField, FormHeader } from "@/components/ui";

// Constants
const RESPONSIVE_BREAKPOINT = 400;
const MIN_TIME_INTERVAL_MINUTES = 30;
const DEFAULT_START_TIME_SLOTS = generateTimeSlots(5, 30, 20, 0);
const DEFAULT_END_TIME_SLOTS = generateTimeSlots(8, 30, 20, 30);

// Types
interface TimeData {
	hour: number;
	minute: number;
	meridiem: string;
	isValid: boolean;
}

export interface AvailabilityFormData {
	callTimeStart: string;
	callTimeEnd: string;
	callDays: string;
}

export interface AvailabilityFormErrors {
	callTimeStart: boolean;
	callTimeEnd: boolean;
	callDays: boolean;
}

interface FormDataProps {
	formData: AvailabilityFormData;
	setFormData: React.Dispatch<React.SetStateAction<any>>;
	formErrors: AvailabilityFormErrors;
	setFormErrors: React.Dispatch<React.SetStateAction<any>>;
}

function AvailabilityForm({ formData, setFormData, formErrors, setFormErrors }: FormDataProps) {
	const [timeSlotsStart] = useState(DEFAULT_START_TIME_SLOTS);
	const [timeSlotsEnd, setTimeSlotsEnd] = useState(DEFAULT_END_TIME_SLOTS);
	const [isNavDivSmall, setIsNavDivSmall] = useState(false);

	const headingRef = useRef<HTMLHeadingElement | null>(null);
	const navDivRef = useRef<HTMLDivElement | null>(null);

	// Handle responsive layout with ResizeObserver
	useEffect(() => {
		const container = navDivRef.current;
		if (!container) return;

		const observer = new ResizeObserver(([entry]) => {
			setIsNavDivSmall(entry.contentRect.width < RESPONSIVE_BREAKPOINT);
		});

		observer.observe(container);
		return () => observer.disconnect();
	}, []);

	// Validate and update time slots when start time changes
	useEffect(() => {
		if (!formData.callTimeStart) return;

		const startTimeData = parseTime(formData.callTimeStart);

		if (!startTimeData.isValid) {
			console.warn(`Invalid start time format: ${formData.callTimeStart}`);
			return;
		}

		// Calculate minimum end time (start time + interval)
		const startHour24 = convertTo24Hour(startTimeData.hour, startTimeData.meridiem);
		const minEndTime = new Date();
		minEndTime.setHours(startHour24, startTimeData.minute, 0, 0);
		minEndTime.setMinutes(minEndTime.getMinutes() + MIN_TIME_INTERVAL_MINUTES);

		// Update available end time slots
		setTimeSlotsEnd(generateTimeSlots(minEndTime.getHours(), minEndTime.getMinutes()));

		// Update end time if it conflicts with start time
		if (formData.callTimeEnd) {
			const endTimeData = parseTime(formData.callTimeEnd);

			if (endTimeData.isValid && isTimeBeforeOrEqual(endTimeData, startTimeData)) {
				const newEndTime = formatTo12Hour(minEndTime.getHours(), minEndTime.getMinutes());
				setFormData((prevData: any) => ({ ...prevData, callTimeEnd: newEndTime }));
			}
		}
	}, [formData.callTimeStart, formData.callTimeEnd, setFormData]);

	return (
		<div ref={navDivRef} className="jF-flex jF-flex-col jF-space-y-4">
			{/* Header */}
			<FormHeader
				icon="clock"
				title="Select Time Availabilities"
				description="Choose your preferred consultation time."
				headingRef={headingRef}
			/>
			{/* Preferred Call Time */}
			<div className="jF-space-y-2">
				<label htmlFor="callTimeStart" className="jF-label">
					Preferred Call Time
				</label>
				<div className="jF-flex jF-flex-row jF-gap-4 jF-justify-between jF-items-center">
					{/* Start Time */}
					<select
						id="callTimeStart"
						name="callTimeStart"
						value={formData.callTimeStart}
						onChange={(e) =>
							setFormData((prevData: any) => ({
								...prevData,
								callTimeStart: e.target.value,
							}))
						}
						className="jF-flex-1 jF-text-center jF-select-focus jF-select-default"
					>
						{timeSlotsStart?.map((slot, index) => (
							<option key={index + slot.option} value={slot.option}>
								{slot.option}
							</option>
						))}
					</select>

					<span className="jF-text-gray-500">to</span>

					{/* End Time */}
					<select
						id="callTimeEnd"
						name="callTimeEnd"
						value={formData.callTimeEnd}
						onChange={(e) =>
							setFormData((prevData: any) => ({
								...prevData,
								callTimeEnd: e.target.value,
							}))
						}
						className="jF-flex-1 jF-text-center jF-select-focus jF-select-default"
					>
						{timeSlotsEnd?.map((slot, index) => (
							<option key={index + slot.option} value={slot.option}>
								{slot.option}
							</option>
						))}
					</select>
				</div>
			</div>

			{/* Preferred Call Days */}
			<div>
				<CheckGroupField
					id="callDays"
					label="Preferred Call Day/s"
					values={formData.callDays ? formData.callDays.split(",") : []}
					onChange={(values) =>
						setFormData((prevData: any) => ({
							...prevData,
							callDays: values.join(","),
						}))
					}
					options={callDays.map((day) => ({
						id: day.id,
						value: day.option,
						label: day.option,
					}))}
					error={"This field is required"}
					required={false}
					disabled={false}
					className={""}
					gridCols={4}
					showIcons={false}
					disabledOptions={[]}
					navRef={isNavDivSmall}
				/>
			</div>
			{formErrors.callDays && (
				<span className="jF-text-red-700 jF-text-xs">This field is required</span>
			)}
		</div>
	);
}

export default memo(AvailabilityForm);

const parseTime = (time: string): TimeData => {
	if (!time || typeof time !== "string") {
		return { hour: 0, minute: 0, meridiem: "AM", isValid: false };
	}

	const [hourPart, minuteAndMeridiem] = time.split(":");
	const [minutePart, meridiemRaw] = (minuteAndMeridiem || "").split(" ");

	const hour = Number(hourPart);
	const minute = Number(minutePart);
	const meridiem = (meridiemRaw || "").trim().toUpperCase();

	const isValid =
		!isNaN(hour) &&
		!isNaN(minute) &&
		hour >= 1 &&
		hour <= 12 &&
		minute >= 0 &&
		minute < 60 &&
		(meridiem === "AM" || meridiem === "PM");

	return { hour, minute, meridiem, isValid };
};

const convertTo24Hour = (hour: number, meridiem: string): number => {
	if (meridiem === "PM" && hour !== 12) return hour + 12;
	if (meridiem === "AM" && hour === 12) return 0;
	return hour;
};

const formatTo12Hour = (hours: number, minutes: number): string => {
	const period = hours < 12 ? "AM" : "PM";
	const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
	const displayMinute = minutes.toString().padStart(2, "0");
	return `${displayHour}:${displayMinute} ${period}`;
};

const isTimeBeforeOrEqual = (time1: TimeData, time2: TimeData): boolean => {
	const totalMinutes1 = convertTo24Hour(time1.hour, time1.meridiem) * 60 + time1.minute;
	const totalMinutes2 = convertTo24Hour(time2.hour, time2.meridiem) * 60 + time2.minute;
	return totalMinutes1 <= totalMinutes2;
};
